import { useState, useCallback, useEffect, useRef } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Button,
  Flex,
  Box,
  Text,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  Divider,
  useToast,
} from '@chakra-ui/react';

import {
  apiRecalculateItv,
  apiDeleteItvRecalculation,
  type ItvData,
  type EzitvAddress,
  type ItvConstructionOverrides,
} from 'api/quote-marketplace';

const PANORAMIC_HEIGHT = 470;

const FOUNDATION_OPTIONS = [
  { value: '', label: 'Select...' },
  { value: 'Basement', label: 'Basement' },
  { value: 'Crawlspace', label: 'Crawlspace' },
  { value: 'Slab', label: 'Slab' },
  { value: 'ConcreteSlab', label: 'Concrete Slab' },
  { value: 'PostPier', label: 'Post/Pier' },
  { value: 'WalkoutBasement', label: 'Walkout Basement' },
];

const ARCHITECTURAL_STYLE_OPTIONS = [
  { value: '', label: 'Select...' },
  { value: 'Detached', label: 'Detached' },
  { value: 'SemiDetached', label: 'Semi-Detached' },
  { value: 'EndRow', label: 'End Row' },
  { value: 'InRow', label: 'In Row' },
  { value: 'Duplex', label: 'Duplex' },
  { value: 'Triplex', label: 'Triplex' },
  { value: 'Fourplex', label: 'Fourplex' },
];

const STOREY_OPTIONS = [
  { value: '', label: 'Select...' },
  { value: '1', label: '1' },
  { value: '1.5', label: '1.5' },
  { value: '2', label: '2' },
  { value: '2.5', label: '2.5' },
  { value: '3', label: '3' },
  { value: '3.5', label: '3.5' },
  { value: '4', label: '4' },
  { value: '5', label: '5 or more' },
];

const KITCHEN_OPTIONS = [
  { value: '', label: 'Select...' },
  { value: '1', label: '1' },
  { value: '2', label: '2' },
  { value: '3', label: '3' },
  { value: '4', label: '4 or more' },
];

const EXTERIOR_WALL_OPTIONS = [
  { value: '', label: 'Select...' },
  { value: 'BrickVeneer', label: 'Brick Veneer' },
  { value: 'Brick', label: 'Brick' },
  { value: 'VinylSiding', label: 'Vinyl Siding' },
  { value: 'WoodSiding', label: 'Wood Siding' },
  { value: 'Aluminium', label: 'Aluminum' },
  { value: 'Stucco', label: 'Stucco' },
  { value: 'Stone', label: 'Stone' },
  { value: 'StoneVeneer', label: 'Stone Veneer' },
  { value: 'Fibreboard', label: 'Fibre Board' },
  { value: 'Logs', label: 'Logs' },
];

const ROOF_TYPE_OPTIONS = [
  { value: '', label: 'Select...' },
  { value: 'Asphalt', label: 'Asphalt' },
  { value: 'Steel', label: 'Steel' },
  { value: 'Wood', label: 'Wood' },
  { value: 'Slate', label: 'Slate' },
  { value: 'Tile', label: 'Tile' },
  { value: 'Concrete', label: 'Concrete' },
  { value: 'Metal', label: 'Metal' },
  { value: 'Rubber', label: 'Rubber' },
];

const FINISHED_BASEMENT_OPTIONS = [
  { value: '', label: 'Select...' },
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];

const GARAGE_TYPE_OPTIONS = [
  { value: '', label: 'Select...' },
  { value: 'None', label: 'None' },
  { value: 'AttachedGarage', label: 'Attached Garage' },
  { value: 'DetachedGarage', label: 'Detached Garage' },
  { value: 'Built-inGarage', label: 'Built-in Garage' },
  { value: 'BasementGarage', label: 'Basement Garage' },
  { value: 'Carport', label: 'Carport' },
];

const HEATING_TYPE_OPTIONS = [
  { value: '', label: 'Select...' },
  { value: 'None', label: 'None' },
  { value: 'Geothermal', label: 'Geothermal' },
  { value: 'Radiant', label: 'Radiant' },
  { value: 'Electric', label: 'Electric' },
  { value: 'Natural Gas Forced Air', label: 'Natural Gas Forced Air' },
  { value: 'Oil', label: 'Oil' },
  { value: 'Water', label: 'Water' },
  { value: 'Wood', label: 'Wood' },
];

const SWIMMING_POOL_OPTIONS = [
  { value: '', label: 'Select...' },
  { value: 'None', label: 'None' },
  { value: 'Inground Concrete', label: 'Inground Concrete' },
  { value: 'Inground Fibre Glass', label: 'Inground Fibre Glass' },
  { value: 'Inground Vinyl', label: 'Inground Vinyl' },
  { value: 'Above Ground', label: 'Above Ground' },
  { value: 'Above Ground Fibre Glass', label: 'Above Ground Fibre Glass' },
];

const PLUMBING_TYPE_OPTIONS = [
  { value: '', label: 'Select...' },
  { value: 'Copper', label: 'Copper' },
  { value: 'Galvanized', label: 'Galvanized' },
  { value: 'Iron', label: 'Iron' },
  { value: 'PVC', label: 'PVC' },
  { value: 'ABS', label: 'ABS' },
  { value: 'Pex', label: 'PEX' },
  { value: 'Unknown', label: 'Unknown' },
];

const OUTBUILDING_OPTIONS = [
  { value: '', label: 'Select...' },
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
  { value: 'Unknown', label: 'Unknown' },
];

const SOLAR_PANELS_OPTIONS = [
  { value: '', label: 'Select...' },
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
  { value: 'Unknown', label: 'Unknown' },
];

interface ItvReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  itvData: ItvData;
  address: EzitvAddress;
  quoteId: number;
  onApply: (itvData: ItvData) => void;
  originalFeatures?: Record<string, string | number | null>;
}

type ConstructionFeatures = NonNullable<ItvData['ConstructionFeatures']>;

const getFeatureValue = (
  features: ConstructionFeatures | undefined,
  key: string,
): string => {
  const val = features?.[key];
  if (val === null || val === undefined) return '';
  return String(val);
};

const ItvReviewModal = ({
  isOpen,
  onClose,
  itvData,
  address,
  quoteId,
  onApply,
  originalFeatures,
}: ItvReviewModalProps) => {
  const toast = useToast();
  const [currentData, setCurrentData] = useState<ItvData>(itvData);
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [modifiedFields, setModifiedFields] = useState<Set<string>>(new Set());
  const [needsRecalculation, setNeedsRecalculation] = useState(false);
  const [hasRecalculated, setHasRecalculated] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const baselineFeatures =
    originalFeatures || itvData.ConstructionFeatures || {};
  const confirmCancelRef = useRef<HTMLButtonElement>(null);
  const currentFeatures = currentData.ConstructionFeatures || {};

  useEffect(() => {
    if (isOpen) {
      setCurrentData(itvData);
      setNeedsRecalculation(false);
      setHasRecalculated(false);

      const baseline = originalFeatures || itvData.ConstructionFeatures || {};
      const current = itvData.ConstructionFeatures || {};
      const changedFields = new Set<string>();

      Object.keys(current).forEach((key) => {
        if (String(current[key] ?? '') !== String(baseline[key] ?? '')) {
          changedFields.add(key);
        }
      });

      setModifiedFields(changedFields);
    }
  }, [isOpen, itvData, originalFeatures]);

  const isFieldChanged = useCallback(
    (key: string) => modifiedFields.has(key),
    [modifiedFields],
  );

  const updateFeature = useCallback(
    (key: string, value: string | number) => {
      const originalValue = baselineFeatures[key];
      const isChanged = String(value ?? '') !== String(originalValue ?? '');

      setModifiedFields((prev) => {
        const next = new Set(prev);
        if (isChanged) {
          next.add(key);
        } else {
          next.delete(key);
        }
        return next;
      });

      setNeedsRecalculation(true);

      setCurrentData((prev) => ({
        ...prev,
        ConstructionFeatures: {
          ...prev.ConstructionFeatures,
          [key]: value,
        },
      }));
    },
    [baselineFeatures],
  );

  const handleRecalculate = async () => {
    const overrides: ItvConstructionOverrides = {};

    const keysToCheck: Array<keyof ItvConstructionOverrides> = [
      'YearBuilt',
      'SquareFootage',
      'FoundationType',
      'ArchitecturalStyleType',
      'StoreyCount',
      'KitchenCount',
      'BathroomCount',
      'ExteriorWallType',
      'RoofType',
      'FinishedBasement',
      'GarageType',
      'PrimaryHeatingType',
      'SwimmingPoolType',
      'PlumbingType',
      'OutbuildingPresent',
      'SolarPanel',
    ];

    keysToCheck.forEach((key) => {
      const currentVal = currentFeatures[key];
      if (currentVal !== undefined && currentVal !== '') {
        if (key === 'SquareFootage') {
          overrides[key] = Number(currentVal);
        } else if (key === 'FinishedBasement') {
          overrides[key] = currentVal === 'true';
        } else {
          (overrides as Record<string, unknown>)[key] = String(currentVal);
        }
      }
    });

    setIsRecalculating(true);
    try {
      const response = await apiRecalculateItv(quoteId, { address, overrides });
      if (response.success && response.itv_data) {
        setCurrentData({
          ...response.itv_data,
          ConstructionFeatures: {
            ...response.itv_data.ConstructionFeatures,
            ...currentFeatures,
          },
        });
        setNeedsRecalculation(false);
        setHasRecalculated(true);
        toast({
          title: 'Valuation updated',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      } else {
        toast({
          title: 'Recalculation failed',
          description: response.error || 'Unable to recalculate valuation',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        });
      }
    } catch {
      toast({
        title: 'Recalculation failed',
        description: 'Unable to connect to iClarify service',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    } finally {
      setIsRecalculating(false);
    }
  };

  const handleApply = () => {
    onApply(currentData);
  };

  const handleCloseAttempt = () => {
    if (needsRecalculation || hasRecalculated) {
      setIsConfirmOpen(true);
    } else {
      onClose();
    }
  };

  const handleConfirmDiscard = async () => {
    setIsProcessing(true);
    try {
      if (hasRecalculated) {
        await apiDeleteItvRecalculation(quoteId);
      }
    } catch {
      // Ignore delete errors, still close the modal
    } finally {
      setIsProcessing(false);
      setIsConfirmOpen(false);
      onClose();
    }
  };

  const handleConfirmApply = async () => {
    setIsProcessing(true);
    try {
      if (needsRecalculation) {
        const overrides: ItvConstructionOverrides = {};
        const keysToCheck: Array<keyof ItvConstructionOverrides> = [
          'YearBuilt',
          'SquareFootage',
          'FoundationType',
          'ArchitecturalStyleType',
          'StoreyCount',
          'KitchenCount',
          'BathroomCount',
          'ExteriorWallType',
          'RoofType',
          'FinishedBasement',
          'GarageType',
          'PrimaryHeatingType',
          'SwimmingPoolType',
          'PlumbingType',
          'OutbuildingPresent',
          'SolarPanel',
        ];

        keysToCheck.forEach((key) => {
          const currentVal = currentFeatures[key];
          const originalVal = baselineFeatures[key];
          if (
            String(currentVal ?? '') !== String(originalVal ?? '') &&
            currentVal !== undefined
          ) {
            if (key === 'SquareFootage') {
              overrides[key] = Number(currentVal);
            } else if (key === 'FinishedBasement') {
              overrides[key] = currentVal === 'true';
            } else {
              (overrides as Record<string, unknown>)[key] = String(currentVal);
            }
          }
        });

        const response = await apiRecalculateItv(quoteId, {
          address,
          overrides,
        });
        if (response.success && response.itv_data) {
          onApply({
            ...response.itv_data,
            ConstructionFeatures: {
              ...response.itv_data.ConstructionFeatures,
              ...currentFeatures,
            },
          });
        } else {
          toast({
            title: 'Recalculation failed',
            description: response.error || 'Unable to recalculate valuation',
            status: 'error',
            duration: 5000,
            isClosable: true,
            position: 'top-right',
          });
          setIsProcessing(false);
          setIsConfirmOpen(false);
          return;
        }
      } else {
        onApply(currentData);
      }
    } catch {
      toast({
        title: 'Failed to apply',
        description: 'Unable to connect to iClarify service',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
      setIsProcessing(false);
      setIsConfirmOpen(false);
      return;
    }
    setIsProcessing(false);
    setIsConfirmOpen(false);
  };

  const confirmTitle = needsRecalculation
    ? 'Unsaved Changes'
    : 'Unapplied Recalculation';

  const confirmMessage = needsRecalculation
    ? 'You have made changes that have not been recalculated. What would you like to do?'
    : 'You have recalculated values that have not been applied. What would you like to do?';

  const confirmApplyLabel = needsRecalculation
    ? 'Recalculate & Apply'
    : 'Apply';

  const formatCurrency = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatCostPerSqFt = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '-';
    return (
      new Intl.NumberFormat('en-CA', {
        style: 'currency',
        currency: 'CAD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value) + '/sq.ft.'
    );
  };

  const panoramicUrl = currentData.panoramic_url;

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleCloseAttempt} size="6xl" isCentered>
        <ModalOverlay />
        <ModalContent maxW="1100px" maxH="90vh">
          <ModalHeader>iClarify Residential</ModalHeader>
          <ModalCloseButton onClick={handleCloseAttempt} />
          <ModalBody overflowY="auto" py={4} maxH="calc(90vh - 140px)">
            <Flex gap={6} direction={{ base: 'column', lg: 'row' }}>
              <Box
                flex={{ base: 'none', lg: '0 0 400px' }}
                w={{ base: '100%', lg: 'auto' }}
                maxW={{ base: '100%', lg: '400px' }}
                order={{ base: 2, lg: 1 }}
                pr={{ base: 0, lg: 2 }}
                pb={2}
              >
                <VStack spacing={3} align="stretch" pb={2}>
                  <Flex gap={3}>
                    <FormControl flex={1}>
                      <FormLabel fontSize="sm">Year Built</FormLabel>
                      <Input
                        variant="main"
                        size="sm"
                        type="number"
                        value={getFeatureValue(currentFeatures, 'YearBuilt')}
                        onChange={(e) =>
                          updateFeature('YearBuilt', e.target.value)
                        }
                        borderColor={
                          isFieldChanged('YearBuilt') ? 'orange.400' : undefined
                        }
                        borderWidth={
                          isFieldChanged('YearBuilt') ? '2px' : undefined
                        }
                      />
                    </FormControl>
                    <FormControl flex={1}>
                      <FormLabel fontSize="sm">Square Feet</FormLabel>
                      <Input
                        variant="main"
                        size="sm"
                        type="number"
                        value={getFeatureValue(
                          currentFeatures,
                          'SquareFootage',
                        )}
                        onChange={(e) =>
                          updateFeature('SquareFootage', e.target.value)
                        }
                        borderColor={
                          isFieldChanged('SquareFootage')
                            ? 'orange.400'
                            : undefined
                        }
                        borderWidth={
                          isFieldChanged('SquareFootage') ? '2px' : undefined
                        }
                      />
                    </FormControl>
                  </Flex>

                  <FormControl>
                    <FormLabel fontSize="sm">Foundation Type</FormLabel>
                    <Select
                      variant="main"
                      size="sm"
                      value={getFeatureValue(currentFeatures, 'FoundationType')}
                      onChange={(e) =>
                        updateFeature('FoundationType', e.target.value)
                      }
                      borderColor={
                        isFieldChanged('FoundationType')
                          ? 'orange.400'
                          : undefined
                      }
                      borderWidth={
                        isFieldChanged('FoundationType') ? '2px' : undefined
                      }
                    >
                      {FOUNDATION_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl>
                    <FormLabel fontSize="sm">Architectural Style</FormLabel>
                    <Select
                      variant="main"
                      size="sm"
                      value={getFeatureValue(
                        currentFeatures,
                        'ArchitecturalStyleType',
                      )}
                      onChange={(e) =>
                        updateFeature('ArchitecturalStyleType', e.target.value)
                      }
                      borderColor={
                        isFieldChanged('ArchitecturalStyleType')
                          ? 'orange.400'
                          : undefined
                      }
                      borderWidth={
                        isFieldChanged('ArchitecturalStyleType')
                          ? '2px'
                          : undefined
                      }
                    >
                      {ARCHITECTURAL_STYLE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Select>
                  </FormControl>

                  <Flex gap={3}>
                    <FormControl flex={1}>
                      <FormLabel fontSize="sm">Building Storeys</FormLabel>
                      <Select
                        variant="main"
                        size="sm"
                        value={getFeatureValue(currentFeatures, 'StoreyCount')}
                        onChange={(e) =>
                          updateFeature('StoreyCount', e.target.value)
                        }
                        borderColor={
                          isFieldChanged('StoreyCount')
                            ? 'orange.400'
                            : undefined
                        }
                        borderWidth={
                          isFieldChanged('StoreyCount') ? '2px' : undefined
                        }
                      >
                        {STOREY_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl flex={1}>
                      <FormLabel fontSize="sm">Kitchens</FormLabel>
                      <Select
                        variant="main"
                        size="sm"
                        value={getFeatureValue(currentFeatures, 'KitchenCount')}
                        onChange={(e) =>
                          updateFeature('KitchenCount', e.target.value)
                        }
                        borderColor={
                          isFieldChanged('KitchenCount')
                            ? 'orange.400'
                            : undefined
                        }
                        borderWidth={
                          isFieldChanged('KitchenCount') ? '2px' : undefined
                        }
                      >
                        {KITCHEN_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </Select>
                    </FormControl>
                  </Flex>

                  <FormControl>
                    <FormLabel fontSize="sm">Bathrooms</FormLabel>
                    <Input
                      variant="main"
                      size="sm"
                      value={getFeatureValue(currentFeatures, 'BathroomCount')}
                      onChange={(e) =>
                        updateFeature('BathroomCount', e.target.value)
                      }
                      placeholder="e.g. 2.5"
                      borderColor={
                        isFieldChanged('BathroomCount')
                          ? 'orange.400'
                          : undefined
                      }
                      borderWidth={
                        isFieldChanged('BathroomCount') ? '2px' : undefined
                      }
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel fontSize="sm">Exterior Wall</FormLabel>
                    <Select
                      variant="main"
                      size="sm"
                      value={getFeatureValue(
                        currentFeatures,
                        'ExteriorWallType',
                      )}
                      onChange={(e) =>
                        updateFeature('ExteriorWallType', e.target.value)
                      }
                      borderColor={
                        isFieldChanged('ExteriorWallType')
                          ? 'orange.400'
                          : undefined
                      }
                      borderWidth={
                        isFieldChanged('ExteriorWallType') ? '2px' : undefined
                      }
                    >
                      {EXTERIOR_WALL_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl>
                    <FormLabel fontSize="sm">Roof Type</FormLabel>
                    <Select
                      variant="main"
                      size="sm"
                      value={getFeatureValue(currentFeatures, 'RoofType')}
                      onChange={(e) =>
                        updateFeature('RoofType', e.target.value)
                      }
                      borderColor={
                        isFieldChanged('RoofType') ? 'orange.400' : undefined
                      }
                      borderWidth={
                        isFieldChanged('RoofType') ? '2px' : undefined
                      }
                    >
                      {ROOF_TYPE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl>
                    <FormLabel fontSize="sm">Finished Basement</FormLabel>
                    <Select
                      variant="main"
                      size="sm"
                      value={getFeatureValue(
                        currentFeatures,
                        'FinishedBasement',
                      )}
                      onChange={(e) =>
                        updateFeature('FinishedBasement', e.target.value)
                      }
                      borderColor={
                        isFieldChanged('FinishedBasement')
                          ? 'orange.400'
                          : undefined
                      }
                      borderWidth={
                        isFieldChanged('FinishedBasement') ? '2px' : undefined
                      }
                    >
                      {FINISHED_BASEMENT_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl>
                    <FormLabel fontSize="sm">Garage Type</FormLabel>
                    <Select
                      variant="main"
                      size="sm"
                      value={getFeatureValue(currentFeatures, 'GarageType')}
                      onChange={(e) =>
                        updateFeature('GarageType', e.target.value)
                      }
                      borderColor={
                        isFieldChanged('GarageType') ? 'orange.400' : undefined
                      }
                      borderWidth={
                        isFieldChanged('GarageType') ? '2px' : undefined
                      }
                    >
                      {GARAGE_TYPE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl>
                    <FormLabel fontSize="sm">Specialty Heating</FormLabel>
                    <Select
                      variant="main"
                      size="sm"
                      value={getFeatureValue(
                        currentFeatures,
                        'PrimaryHeatingType',
                      )}
                      onChange={(e) =>
                        updateFeature('PrimaryHeatingType', e.target.value)
                      }
                      borderColor={
                        isFieldChanged('PrimaryHeatingType')
                          ? 'orange.400'
                          : undefined
                      }
                      borderWidth={
                        isFieldChanged('PrimaryHeatingType') ? '2px' : undefined
                      }
                    >
                      {HEATING_TYPE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl>
                    <FormLabel fontSize="sm">Swimming Pool</FormLabel>
                    <Select
                      variant="main"
                      size="sm"
                      value={getFeatureValue(
                        currentFeatures,
                        'SwimmingPoolType',
                      )}
                      onChange={(e) =>
                        updateFeature('SwimmingPoolType', e.target.value)
                      }
                      borderColor={
                        isFieldChanged('SwimmingPoolType')
                          ? 'orange.400'
                          : undefined
                      }
                      borderWidth={
                        isFieldChanged('SwimmingPoolType') ? '2px' : undefined
                      }
                    >
                      {SWIMMING_POOL_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl>
                    <FormLabel fontSize="sm">Plumbing Type</FormLabel>
                    <Select
                      variant="main"
                      size="sm"
                      value={getFeatureValue(currentFeatures, 'PlumbingType')}
                      onChange={(e) =>
                        updateFeature('PlumbingType', e.target.value)
                      }
                      borderColor={
                        isFieldChanged('PlumbingType')
                          ? 'orange.400'
                          : undefined
                      }
                      borderWidth={
                        isFieldChanged('PlumbingType') ? '2px' : undefined
                      }
                    >
                      {PLUMBING_TYPE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl>
                    <FormLabel fontSize="sm">Outbuilding Present</FormLabel>
                    <Select
                      variant="main"
                      size="sm"
                      value={getFeatureValue(
                        currentFeatures,
                        'OutbuildingPresent',
                      )}
                      onChange={(e) =>
                        updateFeature('OutbuildingPresent', e.target.value)
                      }
                      borderColor={
                        isFieldChanged('OutbuildingPresent')
                          ? 'orange.400'
                          : undefined
                      }
                      borderWidth={
                        isFieldChanged('OutbuildingPresent') ? '2px' : undefined
                      }
                    >
                      {OUTBUILDING_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl>
                    <FormLabel fontSize="sm">Solar Panels</FormLabel>
                    <Select
                      variant="main"
                      size="sm"
                      value={getFeatureValue(currentFeatures, 'SolarPanel')}
                      onChange={(e) =>
                        updateFeature('SolarPanel', e.target.value)
                      }
                      borderColor={
                        isFieldChanged('SolarPanel') ? 'orange.400' : undefined
                      }
                      borderWidth={
                        isFieldChanged('SolarPanel') ? '2px' : undefined
                      }
                    >
                      {SOLAR_PANELS_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                </VStack>
              </Box>

              <Box
                flex={{ base: 'none', lg: 1 }}
                w={{ base: '100%', lg: 'auto' }}
                position={{ base: 'relative', lg: 'sticky' }}
                top={0}
                alignSelf="flex-start"
                order={{ base: 1, lg: 2 }}
              >
                <VStack spacing={3} align="stretch">
                  <Flex
                    direction={{ base: 'row', lg: 'column' }}
                    justify="space-between"
                    align={{ base: 'center', lg: 'stretch' }}
                    gap={3}
                  >
                    <Box>
                      <Text fontWeight="600" fontSize="md">
                        {address.street_address?.toUpperCase()}
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        {address.city?.toUpperCase()},{' '}
                        {address.province?.toUpperCase()}{' '}
                        {address.postal_code?.toUpperCase()}
                      </Text>
                    </Box>

                    <Flex
                      display={{ base: 'flex', lg: 'none' }}
                      gap={4}
                      align="center"
                    >
                      <Box textAlign="right">
                        <Text fontSize="xs" color="gray.500">
                          Cost/SqFt
                        </Text>
                        <Text fontWeight="600" fontSize="sm">
                          {formatCostPerSqFt(
                            currentData.Valuation?.CostPerSquareFoot,
                          )}
                        </Text>
                      </Box>
                      <Box textAlign="right">
                        <Text fontSize="xs" color="gray.500">
                          Valuation
                        </Text>
                        <Text fontWeight="700" fontSize="md" color="green.600">
                          {formatCurrency(
                            currentData.Valuation?.ReplacementCost,
                          )}
                        </Text>
                      </Box>
                    </Flex>
                  </Flex>

                  {panoramicUrl ? (
                    <Box
                      flex={1}
                      minH={{ base: '250px', lg: `${PANORAMIC_HEIGHT}px` }}
                      borderRadius="md"
                      overflow="hidden"
                      borderWidth="1px"
                      borderColor="gray.200"
                    >
                      <iframe
                        title="Panoramic view"
                        src={panoramicUrl}
                        width="100%"
                        height="100%"
                        style={{
                          display: 'block',
                          border: 0,
                          minHeight: 'inherit',
                        }}
                        allowFullScreen
                      />
                    </Box>
                  ) : (
                    <Box
                      flex={1}
                      minH={{ base: '200px', lg: `${PANORAMIC_HEIGHT}px` }}
                      borderRadius="md"
                      borderWidth="1px"
                      borderColor="gray.200"
                      bg="gray.50"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Text color="gray.500">No panoramic view available</Text>
                    </Box>
                  )}

                  <Divider display={{ base: 'none', lg: 'block' }} />

                  <Flex
                    display={{ base: 'none', lg: 'flex' }}
                    justify="space-between"
                    fontSize="sm"
                  >
                    <Text fontWeight="500">Cost Per SqFt:</Text>
                    <Text fontWeight="600">
                      {formatCostPerSqFt(
                        currentData.Valuation?.CostPerSquareFoot,
                      )}
                    </Text>
                  </Flex>
                  <Flex
                    display={{ base: 'none', lg: 'flex' }}
                    justify="space-between"
                    fontSize="md"
                  >
                    <Text fontWeight="500">Valuation:</Text>
                    <Text fontWeight="700" color="green.600">
                      {formatCurrency(currentData.Valuation?.ReplacementCost)}
                    </Text>
                  </Flex>
                </VStack>
              </Box>
            </Flex>
          </ModalBody>
          <ModalFooter gap={3}>
            <Button variant="ghost" onClick={handleCloseAttempt}>
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={handleRecalculate}
              isDisabled={!needsRecalculation}
              isLoading={isRecalculating}
              loadingText="Recalculating..."
            >
              Re-calculate
            </Button>
            <Button
              variant="brand"
              onClick={handleApply}
              isDisabled={isRecalculating || needsRecalculation}
            >
              Apply
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <AlertDialog
        isOpen={isConfirmOpen}
        leastDestructiveRef={confirmCancelRef}
        onClose={() => setIsConfirmOpen(false)}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {confirmTitle}
            </AlertDialogHeader>

            <AlertDialogBody>{confirmMessage}</AlertDialogBody>

            <AlertDialogFooter gap={3}>
              <Button
                ref={confirmCancelRef}
                variant="ghost"
                onClick={() => setIsConfirmOpen(false)}
                isDisabled={isProcessing}
              >
                Cancel
              </Button>
              <Button
                variant="outline"
                onClick={handleConfirmDiscard}
                isLoading={isProcessing}
              >
                Discard
              </Button>
              <Button
                variant="brand"
                onClick={handleConfirmApply}
                isLoading={isProcessing}
              >
                {confirmApplyLabel}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default ItvReviewModal;
